import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useAppDispatch } from '@/store/store-hooks'
import type { MedSummarySourceRef } from './schemas'
import { openMedSummarySourceViewer, type OpenMedSummarySourceViewerOptions } from './open-med-summary-source-viewer'

type SourceLoadError = {
  sourceId: string
  message: string
} | null

function buildMedSummaryOpenTelemetry(source: MedSummarySourceRef, options: OpenMedSummarySourceViewerOptions) {
  return {
    tags: {
      contentViewerFeature: 'medical_billing_summary',
      sourceProvider: source.provider,
      referenceBacked: String(Boolean(options.reference)),
    },
    extra: {
      sourceUserFileId: source.source_id,
      referenceId: options.reference?.id ?? null,
    },
  }
}

export function useOpenMedSummarySource() {
  const dispatch = useAppDispatch()
  const [sourceLoadError, setSourceLoadError] = useState<SourceLoadError>(null)

  const openMedSummarySource = useCallback(
    async (source: MedSummarySourceRef, options: OpenMedSummarySourceViewerOptions = {}) => {
      setSourceLoadError(null)

      try {
        const result = await openMedSummarySourceViewer({
          source,
          options,
          dispatch,
        })

        if (!result.opened) {
          const telemetry = buildMedSummaryOpenTelemetry(source, options)
          Sentry.captureMessage('Medical billing summary source viewer open failed', {
            level: 'warning',
            tags: telemetry.tags,
            extra: {
              ...telemetry.extra,
              errorMessage: result.error,
            },
          })
          setSourceLoadError({ sourceId: source.source_id, message: result.error })
          return false
        }

        dispatch(ContentViewerActions.setContentViewerSession({ session: result.session }))
        return true
      } catch (error) {
        Sentry.captureException(error, buildMedSummaryOpenTelemetry(source, options))
        setSourceLoadError({ sourceId: source.source_id, message: 'Could not load this source file.' })
        return false
      }
    },
    [dispatch]
  )

  return {
    openMedSummarySource,
    sourceLoadError,
  }
}
